<script>
  import { _ } from "svelte-i18n"
  import { scale } from "svelte/transition"
  import { currency_sale, pro_yearly_sale_details } from "../stores"
  export let kind = 'default'     // {'default' | 'accent' | 'accent-orange' | 'outline' | 'transparent' | 'reveal' | 'delete' | 'outline-accent-orange'}
  export let danger = false       // {boolean} [disabled=false] use along with kind
  export let size = 'normal'      // {'normal' | 'small' | 'tiny'}
  export let disabled = false     // {boolean}
  export let busy = false         // {boolean}
  export let type = 'button'      // {'button' | 'submit'}
  export let circle = false       // {boolean}
  export let href = undefined     // {string} (if passed will render anchor instead of button)
  export let target = undefined   // {string} (used with string to open in new window)
  export let on_fill = false      // {boolean} (different text and fill colors on filled backgrounds)
  export let on_warning = false   // {boolean} (effective only on outline buttons on warning backgrounds)
  export let sale_badge = false         // {boolean} (only used for a sale badge on pro account button)
  export let scaled_in = false
  export let title = undefined

  $: discount_percent = Math.ceil($pro_yearly_sale_details?.discountPercent) || "25"

  $: buttonProps = {
    role: "button",
    type: href && !disabled ? undefined : type,
    disabled,
    href,
    title,
    target,
    class: [
      'button',
      kind && `button-${kind}`,
      size && `button-${size}`,
      danger && 'button-danger',
      disabled && "button-disabled",
      busy && "button-busy",
      circle && "button-circle",
      on_fill && "button-on-fill",
      on_warning && "button-on-warning",
      sale_badge && "button-sale",
    ]
    .filter(Boolean)
    .join(" "),
  }
</script>

{#if href && !disabled}
  <!-- svelte-ignore a11y-missing-attribute -->
  <a
    {...buttonProps}
    on:click
    on:mouseover
    on:mouseenter
    on:mouseleave
    >
      {#if $$slots.textIcon}
        <span class="text-icon">
          <slot name="textIcon"></slot>
        </span>
        {/if}
      <slot/>
  </a>
{:else}
  <button
    {...buttonProps}
    on:click
    on:mouseover
    on:mouseenter
    on:mouseleave
    in:scale={{duration: scaled_in ? 500 : 0}}
    data-sale-text={!!$pro_yearly_sale_details ? `${discount_percent}% ${$_('subscriptionPlan.sale')}` : $currency_sale ? $_('subscriptionPlan.currencyDiscount') : ""}
    >
      {#if $$slots.textIcon}
        <span class="text-icon">
          <slot name="textIcon"></slot>
        </span>
        {/if}
      <slot/>
  </button>
{/if}

<style>
  .button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.6rem;
    height: 4rem;
    padding: 0 1.2rem;
    font-family: var(--PensumSans);
    font-size: 1.6rem;
    font-weight: 500;
    line-height: 3.6rem;
    text-decoration: none;
    border: 2px solid transparent;
    outline: none;
    cursor: pointer;
    border-radius: 2rem;
    box-shadow: var(--box-shadow);
    transition: all var(--transition-props);
    font-variant-numeric: lining-nums;
  }

  /* Default */
  .button-default {
    background-color: var(--c-button-bg);
    color: var(--c-text-on-fill);
  }
  .button-default:hover {
    color: var(--c-text-on-fill-hover);
  }

  /* Accent */
  .button-accent {
    background-color: var(--c-button-accent-bg);
    color: var(--c-text-on-fill);
  }
  .button-accent:hover {
    color: var(--c-text-on-fill-hover);
  }

  /* Accent 2 */
  .button-accent-orange {
    background-color: var(--c-accent-orange);
    color: var(--c-accent-orange-text);
  }
  .button-accent-orange:hover {
    background-color: var(--c-accent-orange-hover);
    color: var(--c-accent-orange-text-hover);
  }

  /* Outline and Outline with Accent Orange hover */
  .button-outline,
  .button-outline-accent-orange {
    line-height: 3.6rem;
    color: var(--c-text);
    background-color: transparent;
    border: 2px solid var(--c-button-ol);
    box-shadow: none;
  }
  .button-outline:hover {
    color: var(--c-text-on-fill-hover);
    background-color: var(--c-button-bg);
    border-color: transparent;
    box-shadow: var(--box-shadow);
  }
  .button-outline.button-on-fill {
    color: var(--c-text-on-fill);
  }
  .button-outline.button-on-fill:hover {
    color: var(--c-text-on-fill-hover);
  }
  .button-outline.button-on-warning:not(:hover),
  .button-outline-accent-orange.button-on-warning:not(:hover) {
    color: var(--c-warning-text);
    border-color: var(--c-button-ol-warning);
  }

  .button-outline-accent-orange:hover {
    color: var(--c-accent-orange-text-hover);
    background-color: var(--c-accent-orange);
    border-color: transparent;
    box-shadow: var(--box-shadow);
  }

  /* Transparent */
  .button-transparent {
    padding: 0;
    color: var(--c-text);
    background-color: transparent;
    box-shadow: none;
  }
  .button-transparent:hover {
    color: var(--c-text-hover);
  }
  .button-transparent.button-on-fill {
    color: var(--c-text-on-fill);
  }
  .button-transparent.button-on-fill:hover {
    color: var(--c-text-on-fill-hover);
  }

  /* Reveal */
  .button-reveal {
    color: var(--c-text);
    background-color: transparent;
    box-shadow: none;
  }
  .button-reveal:hover {
    color: var(--c-text-on-fill-hover);
    background-color: var(--c-button-bg);
    border-color: transparent;
    box-shadow: var(--box-shadow);
  }
  .button-reveal.button-on-fill {
    color: var(--c-text-on-fill);
  }
  .button-reveal.button-on-fill:hover {
    color: var(--c-text-on-fill-hover);
  }

  /* Danger */
  .button-danger:hover {
    background-color: var(--c-danger);
    color: var(--c-text-on-fill-hover);
  }
    
  /* Delete */
  .button-delete {
    background-color: var(--c-danger);
    color: var(--c-text-on-fill);
  }
  .button-delete:hover {
    color: var(--c-text-on-fill-hover);
  }

  /* Disabled */
  .button-disabled {
    opacity: 0.35;
    cursor: not-allowed;
    pointer-events: none;
  }

  /* Busy */
  .button-busy {
    animation: blink 1s infinite ease-in-out;
    pointer-events: none;
  }

  /* Size */
  .button-small {
    height: 3rem;
    padding: 0 1rem;
    gap: 0.5rem;
    line-height: 2.4rem;
  }
  .button-tiny {
    height: 2rem;
    padding: 0 0.5rem;
    gap: 0.3rem;
    line-height: 1.4rem;
    font-size: 1.3rem;
  }

  /* Shape */
  .button-circle {
    width: 4rem;
    padding: 0;
  }
  .button-circle.button-small {
    width: 3rem;
  }
  
  /* Text as Icon */
  .text-icon {
    font-size: 1.2em;
    line-height: normal;
    margin-right: 0.5rem;
    margin-left: -0.25rem;
  }

  @keyframes blink {
    0%   { opacity:0.66; }
    50%  { opacity:0.33; }
    100% { opacity:0.66; }
  }

  .button-sale:before {
    content: attr(data-sale-text);
    position: absolute;
    left: 4px;    
    top: -15px;
    transform: rotate(-5deg);
    color: white;
    font-weight: bold;
    font-size: 13px;
    background-color: red;
    line-height: 20px;
    padding: 0 10px;
    box-shadow: 2px 1px 1px rgb(0 0 0 / 20%);
  }

</style>
